import { Weekday } from '@bp/shared-planung-utils';
import { Button, Grid, GridRow, ImportantIcon, Modal, Select, SelectOptionType } from '@bp/ui-components';
import classNames from 'classnames';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MultiValue } from 'react-select';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { RoomItem } from '../../../stores/TimetableVersion/PinboardStoreTypes';
import {
  useCreateTimetableCardsMutation,
  useDeleteTimetableCardsMutation,
} from '../../../types/planung-graphql-client-defs';
import { createIntArray, uniqueBy } from '../../../utils/arrayFunc';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { useRooms } from '../../Rooms/useRooms';
import { CardType } from '../../TimetableVersion/graphql/types';
import { useTimetableStore } from '../TimetableProvider';
import styles from './CardForm.module.scss';

type CardFormProps = {
  card: CardType;
  onClose: () => void;
  className?: string | undefined;
};

export const CardForm = observer(({ card, onClose, className }: CardFormProps) => {
  const { roomData } = useRooms();
  const context = useMemorizedCacheTag('LESSONS');
  const pinboardStore = useTimetableStore();
  const [, createCards] = useCreateTimetableCardsMutation();
  const [, deleteCards] = useDeleteTimetableCardsMutation();

  const [conflictRoomItem, setConflictRoomItem] = useState<SelectOptionType | null>(null);
  const [overrides, setOverrides] = useState<SelectOptionType[]>([]);
  const [roomValues, setRoomValues] = useState<SelectOptionType[]>([]);
  const [touched, setTouched] = useState<boolean>(false);

  const timeGridEntries = pinboardStore.placedCardsTimeGridEntries.get(card.uuid);

  const overrideCardUuids = overrides
    ? overrides.flatMap((o) => {
        return timeGridEntries
          ? timeGridEntries.flatMap((tge) => {
              const cards = pinboardStore.cardsBySlotAndResource.get(`${tge}:${card.weekday as Weekday}:${o.value}`);
              // filter our own card
              const cardsArray = cards ? Array.from(cards) : [];
              return cardsArray.filter((c) => c !== card.uuid);
            })
          : [];
      })
    : null;

  const _cardRooms = pinboardStore.cardRooms.get(card.uuid);
  const initialCardRooms: SelectOptionType[] = [];
  _cardRooms?.forEach((cr) => {
    initialCardRooms.push({
      label: cr.label,
      value: cr.value,
    });
  });

  useEffect(() => {
    setRoomValues(initialCardRooms);
  }, []);

  const groupedRooms = pinboardStore.defaultCardRooms.get(card.uuid) ?? {
    lessonRoomItems: [],
    subjectRoomItems: [],
    classRoomItems: [],
    teacherRoomItems: [],
  };

  const rooms = useMemo(() => {
    // Raum-Hierarchie: Raumvorrat > Fachraum > Lehrerraum > Klassenraum (dann übrige Schulräume).
    // Jede Vorrats-Gruppe bekommt ihre übersetzte Zwischenüberschrift (group), damit das Dropdown
    // die Räume aus den Vorräten gruppiert anzeigt. Der bereits an der Karte hängende Raum ist der
    // tatsächlich zugewiesene Raum und steckt in `roomValues` (Select-Value).
    const withGroup = (items: RoomItem[], group: string) => items.map((room) => ({ ...room, group }));
    const initialRooms = [
      ...withGroup(groupedRooms.lessonRoomItems, t('rooms.title.lesson')),
      ...withGroup(groupedRooms.subjectRoomItems, t('rooms.title.subject')),
      ...withGroup(groupedRooms.teacherRoomItems, t('rooms.title.teacher')),
      ...withGroup(groupedRooms.classRoomItems, t('rooms.title.class')),
    ];

    // add all missing classrooms from the school
    const additionalRooms =
      roomData?.rooms
        .filter((room) => !!room.classroom)
        .filter((room) => initialRooms.every((r) => r.value !== room.uuid))
        .map((room) => {
          return {
            value: room.uuid,
            label: room.name,
            group: t('common.school'),
            inUse: false,
            buildingName: room.building?.name,
          };
        }) ?? [];
    return [...initialRooms, ...additionalRooms];
  }, [groupedRooms, roomData]);

  const roomOptions: SelectOptionType[] = useMemo(
    () =>
      uniqueBy<RoomItem>(rooms, 'value').map(({ value, label, group, buildingName }) => {
        const inUse = timeGridEntries
          ? timeGridEntries.some((tge) => {
              const cards = pinboardStore.cardsBySlotAndResource.get(`${tge}:${card.weekday as Weekday}:${value}`);
              // filter our own card
              const cardsArray = cards ? Array.from(cards) : [];
              return cardsArray.filter((c) => c !== card.uuid).length > 0;
            })
          : false;

        // Anzeigeoption "Räume nach Gebäuden gruppieren" ersetzt die Gruppierung nach Raum-Herkunft.
        const optionGroup = pinboardStore.groupRoomsByBuilding ? buildingName?.trim() || t('common.noGroup') : group;

        return { label, value, group: optionGroup, highlight: inUse ? 'error' : undefined };
      }),
    [
      rooms,
      card.weekday,
      pinboardStore.cardsBySlotAndResource.get,
      pinboardStore.groupRoomsByBuilding,
      timeGridEntries,
      card.uuid,
    ],
  );

  const handleSubmit = async () => {
    if (overrideCardUuids && overrideCardUuids.length > 0) {
      void pinboardStore.removeRoomsFromCards(
        overrideCardUuids?.map((o) => ({
          uuid: o,
        })),
        overrides.map((o) => {
          return {
            value: o.value as string,
          };
        }),
      );
    }

    void pinboardStore.setRoomsForCards(
      [card],
      roomValues.map((r) => ({
        label: r.label ?? '',
        value: r.value as string,
        inUse: false,
        group: '',
      })),
    );

    resetForm();
    onClose();
  };

  const convertToSingle = useCallback(async () => {
    // No destructive card create/delete while the board is read-only or a solver job runs.
    if (!pinboardStore.isEditable) return;
    let cardsToCreateCounter = 0;
    const cardsToDeleteUuids: string[] = [];

    if (card && card.duration && card.duration > 1) {
      cardsToDeleteUuids.push(card.uuid);
      cardsToCreateCounter = cardsToCreateCounter + card.duration;
    } else {
      return;
    }

    const versionUuid = pinboardStore.getCurrentVersion()?.uuid;
    const lessonUuid = card.lesson?.uuid;
    if (!versionUuid || !lessonUuid) return;

    const created = await createCards(
      {
        versionUuid,
        lessonUuid,
        durations: createIntArray(cardsToCreateCounter).map(() => 1),
        subjectContainerUuid: card.isSubjectContainer ? (card.subject?.uuid ?? null) : null,
      },
      context,
    );
    // Erst löschen, wenn die Einzelkarten angelegt sind: sonst gingen die Stunden verloren. Fehler
    // kommen hier als fehlende Daten an (der Client zeigt sie selbst an).
    if (!created.data?.createTimetableCards.length) return;

    await deleteCards({ cardUuids: cardsToDeleteUuids }, context);
    // Die neuen Karten kennt nur das Backend: Stecktafel neu laden.
    pinboardStore.reloadVersionData();
  }, [card, context, createCards, deleteCards, pinboardStore]);

  function resetForm() {
    setRoomValues([]);
    setConflictRoomItem(null);
    setTouched(false);
  }

  function onAssignDouble() {
    if (conflictRoomItem) {
      setRoomValues([...roomValues, conflictRoomItem]);
    }
    setConflictRoomItem(null);
    setTouched(true);
  }

  function onRemoveOtherRooms() {
    if (conflictRoomItem) {
      setRoomValues([...roomValues, conflictRoomItem]);
      setOverrides((prev) => {
        return [...prev, conflictRoomItem];
      });
    }
    setConflictRoomItem(null);
    setTouched(true);
  }

  function onCancelConflict() {
    if (conflictRoomItem) {
      setRoomValues((prev) => prev.filter((room) => room.value !== conflictRoomItem.value));
    }
    setConflictRoomItem(null);
    setTouched(false);
  }

  const classes = classNames(styles['card-form'], className);

  return (
    <>
      <form className={classes} onSubmit={(e) => e.preventDefault()}>
        <Grid useFormGap>
          <GridRow headline={t('rooms.title', { count: 2 })} spacingBottom='l' direction='column'>
            <GridRow spacingBottom='s'>
              <Select
                isMulti
                isSearchable
                hasGroups
                options={roomOptions}
                onChange={(option) => {
                  const _selectedRooms = roomOptions.filter((roomOption) =>
                    (option as MultiValue<SelectOptionType>).some(({ value }) => value === roomOption.value),
                  );
                  const lastAdded = _selectedRooms.find((sr) => {
                    return !roomValues.some((rv) => rv.value === sr.value);
                  });

                  const lastRemoved = roomValues.find((rv) => {
                    return !_selectedRooms.some((sr) => sr.value === rv.value);
                  });

                  if (lastRemoved && lastRemoved.highlight === 'error') {
                    setOverrides((prev) => {
                      const newOverrides = prev.filter((o) => o.value !== lastRemoved.value);
                      return [...newOverrides];
                    });
                  }

                  if (lastAdded && lastAdded.highlight === 'error') {
                    setConflictRoomItem(lastAdded);
                  } else {
                    setRoomValues(_selectedRooms);
                    setTouched(true);
                  }
                }}
                value={roomValues}
                // Epochenschienen-Karten bekommen ihre Räume aus dem Epochenplan und dürfen keine
                // eigenen zugewiesen bekommen (siehe canHaveRooms im CardStore).
                disabled={!card.startTimeGridEntry || card.isSubjectContainer}
                name='rooms'
                label={t('pinboard.edit.assignRoom')}
              />
            </GridRow>
            {card.isSubjectContainer && (
              <GridRow className={styles.overrides} spacingTop='none' direction='column'>
                <div className={styles.warning}>
                  <ImportantIcon className={styles.icon} />
                  {t('pinboard.edit.subjectContainerRoomsHint')}
                </div>
              </GridRow>
            )}
            {overrides && overrides.length > 0 && (
              <GridRow className={styles.overrides} spacingTop='none' direction='column'>
                <div className={styles.warning}>
                  <ImportantIcon className={styles.icon} />
                  {t('pinboard.edit.overrideRoomsHint')}
                </div>
                <div className={styles.cards}></div>
              </GridRow>
            )}
          </GridRow>
          <GridRow headline={t('pinboard.edit.lesson')} spacingBottom='l'>
            <Button
              hierarchy='secondary'
              onClick={async () => {
                await convertToSingle();
              }}
              disabled={!!card.startTimeGridEntry || (!!card.duration && card.duration <= 1)}
              isLoading={false}
            >
              {t('pinboard.actions.singleHour')}
            </Button>
          </GridRow>
        </Grid>

        <ModalBottomButtons
          closeButton={{
            text: t('common.cancelChanges'),
            callback: () => {
              resetForm();
              onClose();
            },
          }}
          submitButton={{
            disabled: !touched,
            callback: handleSubmit,
          }}
        />
      </form>

      <Modal
        isOpen={!!conflictRoomItem}
        onRequestClose={() => onCancelConflict()}
        title={t('pinboard.roomConflict.alreadyAssigned')}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        hideCloseButton
        style={{ overlay: { zIndex: `calc(var(--z-index-modal) + 1)` } }}
        className={styles['room-conflict-modal']}
      >
        <div className='mb-2'>{t('pinboard.roomConflict.alreadyAssignedCards', { room: conflictRoomItem?.label })}</div>
        <div className={styles.cards}></div>
        <div>{t('common.howToSolve')}</div>
        <div className={styles.actions}>
          <Button hierarchy='secondary' onClick={() => onAssignDouble()}>
            {t('pinboard.roomConflict.assignDouble')}
          </Button>
          <Button className={styles['long-button']} hierarchy='secondary' onClick={() => onRemoveOtherRooms()}>
            {t('pinboard.roomConflict.removeOtherRooms')}
          </Button>
          <Button hierarchy='secondary' onClick={() => onCancelConflict()}>
            {t('common.cancel')}
          </Button>
        </div>
      </Modal>
    </>
  );
});
